/**
 * 紙の書式（#78、docs/31、044）。見積書・請求書の紙の見た目を「書式」として選ぶ。
 *   1回目（無料の部分）：プリセットから選ぶ（縦3・横2。#216 で縦4・横3、#221 で縦4・横4）。会社の既定 → 顧客ごとの既定 → 出すとき、の順で決まり、出した時点で固める。
 *   プリセットは「流し込み型」＝いまの紙（pdf/quote-html.ts）に色・罫線・文字の違いを重ねたもの。標準（縦）はいまの紙そのもの。
 *   自分の書式（部品の自由配置・専用帳票）は2回目（エディタ）から。ref の形は `template:<uuid>` で先に決めてある
 */
import { z } from "zod";
import { Version } from "./concurrent.js";
import { PaperLayout, PaperLayoutSummary } from "./paperLayout.js";
import { AxesSnapshot } from "./estimateAxes.js";
import { TakeoffSnapshot } from "./takeoff.js";
import { DEFAULT_LINE_LABELS, LineLabels } from "./vocabulary.js";

export const PAPER_MSG = {
  refInvalid: "書式の指定が正しくありません",
  presetUnknown: "その書式はありません",
  issued: "出した紙の書式は変えられません",
  adminOnly: "会社の既定の書式を変えられるのは管理者だけです",
  saved: "書式を保存しました",
} as const;

/** 書式の指定。preset:<key>（現場力のプリセット）か template:<uuid>（自分の書式。2回目から） */
export const PaperRef = z.string().regex(/^(preset:[a-z0-9-]+|template:[0-9a-f-]{36})$/, PAPER_MSG.refInvalid);
export type PaperRef = z.infer<typeof PaperRef>;

/** PaperKind・PaperOrient は paperBase.ts（paperLayout.ts と共有。循環しないように） */
export { PAPER_KIND_NAME, PaperKind, PaperOrient } from "./paperBase.js";
import { PaperKind, PaperOrient } from "./paperBase.js";

/**
 * プリセットのテーマ（流し込み型の紙に重ねる違い）。空なら標準の紙そのもの。
 *   titleFont: 題字の書体（serif＝明朝、sans＝ゴシック）／accent: 題字・枠の色／rule: 罫線の太い線の色
 *   headFill: 明細の見出し行の地色（'' で無し）／zebra: 1行おきの地色／vlines: 列の間の線を引くか
 *   numLarge: 数量・単価・金額を大きく（数字大きめ（横）。#142 段6 No.31 で「工事（横）」から）／rowPad: 行の上下の余白（mm）
 *   ボックス（#216。試作 v2 の形を流し込み型に近づける。どれも既定は「何もしない」＝今のプリセットの紙は1文字も変わらない）：
 *     amountFill: 金額の枠の地色（'' で無し）／amountColor: 金額の枠の文字の色（'' なら accent）／amountBorder: rule＝上下の線（標準）・double＝二重の囲み
 *     gridColor: 明細の行と列の間の線の色（'' なら標準＝行 墨・列 灰）
 */
export const PaperTheme = z.object({
  titleFont: z.enum(["serif", "sans"]).default("serif"),
  titleBorder: z.enum(["double", "solid", "none"]).default("double"),
  accent: z.string().default("#1a1a1a"),
  rule: z.string().default("#1a1a1a"),
  headFill: z.string().default("#f0f0f0"),
  zebra: z.string().default(""),
  vlines: z.boolean().default(true),
  numLarge: z.boolean().default(false),
  rowPad: z.number().default(1.2),
  amountFill: z.string().default(""),
  amountColor: z.string().default(""),
  amountBorder: z.enum(["rule", "double"]).default("rule"),
  gridColor: z.string().default(""),
});
export type PaperTheme = z.infer<typeof PaperTheme>;

export const PaperPresetKey = z.enum(["std-p", "simple-p", "classic-p", "box-p", "breakdown-l", "breakdown-l2", "works-l", "box-l", "combo-p", "std-l"]);
export type PaperPresetKey = z.infer<typeof PaperPresetKey>;

export type PaperPreset = { key: PaperPresetKey; name: string; desc: string; orient: PaperOrient; theme: PaperTheme; /** 使える帳票（領収書は標準（縦）だけ＝領収書の紙はテーマを持たない。046） */ kinds: readonly PaperKind[] };

/** ボックス（#216）：金額を灰色の地・二重の囲み・藍の文字に、見出し行は地色、罫線は薄く（試作 v2 の形。自分の書式の複製元は packages/paper の boxEls） */
const BOX_THEME: Partial<PaperTheme> = { titleBorder: "none", rule: "#8c8880", headFill: "#efeee9", gridColor: "#c8c5bc", amountFill: "#efeee9", amountColor: "#123c63", amountBorder: "double" };

const T = (t: Partial<PaperTheme> = {}): PaperTheme => PaperTheme.parse(t);
const QI: readonly PaperKind[] = ["quote", "invoice"];
const ALL: readonly PaperKind[] = ["quote", "invoice", "receipt", "order"];
const ORD: readonly PaperKind[] = ["order"];

/**
 * プリセット（縦4・横4）。標準（縦）＝いまの紙、内訳（横）＝9/23 の横（名称・規格・備考を広く）、ボックス（#216）＝試作 v2 の形。並びは画面の並び。
 * 標準（A4横 表紙＋内訳(2)）（#221）＝標準（A4横 表紙＋内訳）の明細表の名称と規格を同じ幅にしたもの（列の幅の違いは packages/paper の presets.ts）
 */
export const PAPER_PRESETS: readonly PaperPreset[] = [
  { key: "std-p", name: "標準（A4縦）", desc: "二重罫線・明朝の題字", orient: "portrait", theme: T(), kinds: ALL },
  { key: "simple-p", name: "シンプル（A4縦）", desc: "罫線少なめ・ゴシック。1行おきに薄い地色", orient: "portrait", theme: T({ titleFont: "sans", titleBorder: "solid", headFill: "", zebra: "#f4f3ef", vlines: false, rule: "#8a8a8a" }), kinds: QI },
  { key: "classic-p", name: "クラシック（A4縦）", desc: "藍の枠をしっかり", orient: "portrait", theme: T({ accent: "#123c63", rule: "#123c63", headFill: "#e7eef4" }), kinds: QI },
  { key: "box-p", name: "ボックス（A4縦）", desc: "金額を灰色の枠に。見出し行に地色", orient: "portrait", theme: T(BOX_THEME), kinds: QI },
  { key: "breakdown-l", name: "標準（A4横 表紙＋内訳）", desc: "A4横。名称・規格・備考を広く", orient: "landscape", theme: T(), kinds: QI },
  { key: "breakdown-l2", name: "標準（A4横 表紙＋内訳(2)）", desc: "名称と規格の列を同じ幅にした標準（A4横 表紙＋内訳）", orient: "landscape", theme: T(), kinds: QI },
  { key: "works-l", name: "数字大きめ（A4横）", desc: "A4横。数量・単価・金額を大きく、行を高く", orient: "landscape", theme: T({ numLarge: true, rowPad: 1.8 }), kinds: QI },
  { key: "box-l", name: "ボックス（A4横）", desc: "A4横。金額を灰色の枠に。見出し行に地色", orient: "landscape", theme: T(BOX_THEME), kinds: QI },
  // {order}（#233 2d、docs/93 §14.6）：標準（A4縦）＝今の紙そのまま・{order}兼請書（下に切り取り線と請書の欄）・標準（A4横）。名前の {order} は画面が t() で埋める
  { key: "combo-p", name: "{order}兼請書（A4縦）", desc: "下に切り取り線と請書の欄。{vendor}が署名して返送", orient: "portrait", theme: T(), kinds: ORD },
  { key: "std-l", name: "標準（A4横）", desc: "A4横。名称・規格・備考を広く", orient: "landscape", theme: T(), kinds: ORD },
  // 「案内図つき（縦）」（#133、map-p）は #137（2026-10-04）で取り外した。出した紙の snapshot に残る preset:map-p は presetOfRef が標準（縦）として読む
];

export const DEFAULT_PAPER_REF: PaperRef = "preset:std-p";

/**
 * プリセットの見本の絵（#216）の置き場所。見本のデータで本物の流し込み型の紙を描いた PNG（scripts/paper-thumbs.sh が作り、
 * apps/web/public/paper-thumbs/ にコミットする）。製品ごと（見本の中身と言葉が違う）・帳票ごと（題字が違う）
 */
export const paperThumbPath = (product: "genba" | "eigyo", kind: PaperKind, key: PaperPresetKey): string => `/paper-thumbs/${product}/${kind}-${key}.png`;

export function paperPreset(key: string): PaperPreset | undefined {
  return PAPER_PRESETS.find((p) => p.key === key);
}
/** ref からプリセットを引く。template:… や知らない key は標準 */
export function presetOfRef(ref: string | null | undefined): PaperPreset {
  const key = ref?.startsWith("preset:") ? ref.slice(7) : "";
  return paperPreset(key) ?? PAPER_PRESETS[0]!;
}
/** 紙の向きから、いちばん近いプリセット（9/23 の「縦向き｜横向き」を書式に吸収するための読み替え） */
export function presetForOrient(orient: PaperOrient): PaperPresetKey {
  return orient === "landscape" ? "breakdown-l" : "std-p";
}

/** 由来：partner＝顧客ごとの指定／tenant＝会社の既定／system＝標準／draft＝この下書きで選んだ／issued＝出した時点で固めた */
export const PaperSource = z.enum(["partner", "tenant", "system", "draft", "issued"]);
export type PaperSource = z.infer<typeof PaperSource>;

/** 見積・請求書に付く書式の情報（GET /estimates/:id・/invoices/:id の paper） */
export const PaperInfo = z.object({
  ref: PaperRef,
  source: PaperSource,
  preset: PaperPresetKey,
  orient: PaperOrient,
  name: z.string(),
  /** 出したあと（固めてあるので変えられない） */
  frozen: z.boolean(),
  /** 自分の書式（template:…。045）。preset は意味を持たない */
  own: z.boolean().default(false),
});
export type PaperInfo = z.infer<typeof PaperInfo>;

/** 出した時点で固める中身（document_revisions.template_snapshot、v2）。プリセットの中身（向き・テーマ）も写しておく＝あとでプリセットを直しても出した紙は変わらない */
export const PaperSnapshot = z.object({
  v: z.literal(2),
  ref: PaperRef,
  source: PaperSource,
  preset: PaperPresetKey,
  orient: PaperOrient,
  theme: PaperTheme.optional(),
  /** 自分の書式（045）：書式の名前と中身をまるごと写す＝あとで直しても出した紙は変わらない */
  name: z.string().optional(),
  layout: PaperLayout.optional(),
  /** 並べ方と、そのときの工種・場所の名前・「〃」・範囲の注記の設定（#129）。出したあとで名前や設定を変えても紙は変わらない。無ければ手で並べる */
  axes: AxesSnapshot.optional(),
  /** 積算表を付けるか（会社の設定を固めた値）と、出した時点の拾い出し（#130）。無ければ 付けない・いまの拾い出し */
  takeoff: TakeoffSnapshot.optional(),
  /**
   * 紙の描き手の版（#217）。2＝packages/paper の1本の描き手（プリセットも書式の中身 layout ごと固める）。
   * 無ければ #217 より前に出した紙＝プリセットは流し込み型（pdf/quote-html.ts）、自分の書式は前の packages/paper（render-v1）で描く＝出した紙は描き直さない
   */
  engine: z.literal(2).optional(),
  /**
   * 明細の列の見出し（#222、docs/22 §6）：出した時点の会社の言葉（名称・規格・備考）。あとで設定を変えても出した紙は変わらない。
   * 20261020_2000 で今まで出した回（見積書・請求書・領収書）には 名称／規格／備考 を埋めた。無ければ 名称／規格／備考（snapshotLineLabels）。
   * 版 2 の紙は layout の列の見出しにも同じ言葉が入っている（プリセットは会社の言葉で起こす）。流し込み型の紙（#217 より前の紙・変更点・積算表・領収書）がこれを読む
   */
  lineLabels: LineLabels.optional(),
  /**
   * 明細表の下を空の行でページの下まで埋める紙の版（#237、docs/94 A-⑤）。1＝領収書（流し込み型の紙）を #237 から出した回。
   * 無ければ前に出した回＝空の行なし（出した紙は1バイトも変えない。保存の名前も前のまま）
   */
  fill: z.literal(1).optional(),
});
export type PaperSnapshot = z.infer<typeof PaperSnapshot>;
/** 出した紙の明細の列の見出し（無ければ 名称／規格／備考。今の会社の言葉は使わない＝出した紙は変わらない） */
export const snapshotLineLabels = (s: Pick<PaperSnapshot, "lineLabels"> | null | undefined): LineLabels => ({ ...DEFAULT_LINE_LABELS, ...(s?.lineLabels ?? {}) });

/** 紙の描き手の版（#217）。保存する PDF のファイル名に入る。描き手の形を変えたら上げる（出した紙は snapshot の版で描く） */
export const PAPER_ENGINE = 2 as const;
/** 新しい描き手の中の見た目の版（#217 で 1）。上げると下書き・まだ保存していない出した紙の PDF を作り直す */
export const PAPER_ENGINE_REV = 1;

/** PUT /estimates/:id/paper・/invoices/:id/paper・/receipts/:id/paper（下書きだけ）。null＝会社・顧客の既定に戻す */
export const PaperChoice = z.object({
  ref: PaperRef.nullable(),
  /** 版（#202）：帳票の updatedAt。渡すと、いまの版と違えば 409（*_STALE）。省けば見ない。書いたら帳票の版を進める */
  version: Version.optional(),
});
export type PaperChoice = z.infer<typeof PaperChoice>;

/** 会社の既定（PUT /paper/defaults）・顧客ごとの既定（PUT /partners/:id/paper）。null＝上に従う */
export const PaperDefaults = z.object({ quote: PaperRef.nullable().optional(), invoice: PaperRef.nullable().optional(), receipt: PaperRef.nullable().optional(), order: PaperRef.nullable().optional() });
export type PaperDefaults = z.infer<typeof PaperDefaults>;

export const PaperDefaultsView = z.object({ quote: PaperRef.nullable(), invoice: PaperRef.nullable(), receipt: PaperRef.nullable(), order: PaperRef.nullable() });
export type PaperDefaultsView = z.infer<typeof PaperDefaultsView>;

export const PaperPresetList = z.object({
  presets: z.array(
    z.object({
      key: PaperPresetKey, name: z.string(), desc: z.string(), orient: PaperOrient, kinds: z.array(PaperKind),
      /** 見本の絵（#216）：帳票ごとの PNG の URL（paperThumbPath）。設定 → 帳票デザインと紙の画面の「書式」で名前の横に出す */
      thumbs: z.object({ quote: z.string().optional(), invoice: z.string().optional(), receipt: z.string().optional(), order: z.string().optional() }).default({}),
    }),
  ),
  /** 自分の書式（045・046）。枠の順。無ければ空 */
  own: z.object({ quote: z.array(PaperLayoutSummary), invoice: z.array(PaperLayoutSummary), receipt: z.array(PaperLayoutSummary), order: z.array(PaperLayoutSummary) }),
});
export type PaperPresetList = z.infer<typeof PaperPresetList>;
